import React, { useId } from 'react';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';

const DIACRITICS = /[̀-ͯ]/g;

const foldChar = (char: string) =>
  char.normalize('NFD').replace(DIACRITICS, '').toLowerCase();

const fold = (text: string) => Array.from(text).map(foldChar).join('');

export const matchesQuery = (text: string, query: string) =>
  fold(text).includes(fold(query.trim()));

export const highlightQuery = (
  text: string,
  query: string,
): React.ReactNode => {
  const chars = Array.from(text);
  const folded = chars.map(foldChar);
  const needle = fold(query.trim());
  if (!needle || folded.some((char) => char.length !== 1)) {
    return text;
  }
  const start = folded.join('').indexOf(needle);
  if (start < 0) {
    return text;
  }
  const end = start + needle.length;
  return (
    <>
      {chars.slice(0, start).join('')}
      <strong className="mpf-side-menu__filter-match">
        {chars.slice(start, end).join('')}
      </strong>
      {chars.slice(end).join('')}
    </>
  );
};

interface MenuFilterProps {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  resultCount: number | null;
}

export const MenuFilter: React.FC<MenuFilterProps> = ({
  label,
  placeholder,
  value,
  onChange,
  resultCount,
}) => {
  const inputId = useId();

  return (
    <div className="mpf-side-menu__filter" role="search">
      <label htmlFor={inputId} className="mpf-side-menu__filter-label">
        {label}
      </label>
      <div className="mpf-side-menu__filter-field">
        <MagnifyingGlassIcon
          className="mpf-side-menu__filter-icon"
          aria-hidden="true"
        />
        <input
          id={inputId}
          type="search"
          className="mpf-side-menu__filter-input"
          placeholder={placeholder}
          value={value}
          autoComplete="off"
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
      <p className="sr-only" aria-live="polite">
        {resultCount === null
          ? ''
          : resultCount === 1
            ? '1 resultado'
            : `${resultCount} resultados`}
      </p>
    </div>
  );
};

export default MenuFilter;
